"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import {
  HomeIcon,
  PlusCircleIcon,
  ClipboardDocumentListIcon,
  UserCircleIcon,
  MagnifyingGlassIcon,
  CubeIcon,
  ChartBarIcon,
  Cog6ToothIcon,
  ChevronUpIcon,
} from "@heroicons/react/24/outline";
import {
  HomeIcon as HomeIconSolid,
  PlusCircleIcon as PlusCircleIconSolid,
  ClipboardDocumentListIcon as ClipboardDocumentListIconSolid,
  UserCircleIcon as UserCircleIconSolid,
  MagnifyingGlassIcon as MagnifyingGlassIconSolid,
  CubeIcon as CubeIconSolid,
  ChartBarIcon as ChartBarIconSolid,
  Cog6ToothIcon as Cog6ToothIconSolid,
} from "@heroicons/react/24/solid";
import { useAuth } from "@/contexts/AuthContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import Image from "next/image";
import { useMobileMenu } from "@/contexts/MobileMenuContext";
import { useSellerListingAttentionContext } from "@/contexts/SellerListingAttentionContext";
import { useAdminProductsAttentionContext } from "@/contexts/AdminProductsAttentionContext";
import { SellerMyItemsAttentionNavIndicator } from "@/components/nav/SellerMyItemsAttentionNavIndicator";
import { lightTap } from "@/lib/haptics";
import type { ComponentType, SVGProps } from "react";

type NavIcon = ComponentType<SVGProps<SVGSVGElement>>;

type BottomNavItem = {
  name: string;
  href: string;
  icon: NavIcon;
  iconActive: NavIcon;
  requireAuth: boolean;
  isProfile?: boolean;
};

const consumerMenuItems: BottomNavItem[] = [
  {
    name: "Home",
    href: "/",
    icon: HomeIcon,
    iconActive: HomeIconSolid,
    requireAuth: false,
  },
  {
    name: "Marketplace",
    href: "/market",
    icon: MagnifyingGlassIcon,
    iconActive: MagnifyingGlassIconSolid,
    requireAuth: false,
  },
  {
    name: "Sell",
    href: "/sell",
    icon: PlusCircleIcon,
    iconActive: PlusCircleIconSolid,
    requireAuth: false,
  },
  {
    name: "My stuff",
    href: "/my-stuff",
    icon: ClipboardDocumentListIcon,
    iconActive: ClipboardDocumentListIconSolid,
    requireAuth: true,
  },
  {
    name: "Profile",
    href: "/profile",
    icon: UserCircleIcon,
    iconActive: UserCircleIconSolid,
    requireAuth: true,
    isProfile: true,
  },
];

const AUTHED_CONSUMER_ORDER: readonly string[] = [
  "/",
  "/market",
  "/sell",
  "/my-stuff",
  "/profile",
];

const GUEST_CONSUMER_ORDER: readonly string[] = ["/", "/sell", "/market"];

const adminMobileNav: BottomNavItem[] = [
  {
    name: "Dashboard",
    href: "/admin",
    icon: HomeIcon,
    iconActive: HomeIconSolid,
    requireAuth: true,
  },
  {
    name: "Products",
    href: "/admin/products",
    icon: CubeIcon,
    iconActive: CubeIconSolid,
    requireAuth: true,
  },
  {
    name: "Sell",
    href: "/sell",
    icon: PlusCircleIcon,
    iconActive: PlusCircleIconSolid,
    requireAuth: false,
  },
  {
    name: "Reports",
    href: "/admin/reports",
    icon: ChartBarIcon,
    iconActive: ChartBarIconSolid,
    requireAuth: true,
  },
  {
    name: "Settings",
    href: "/admin/settings",
    icon: Cog6ToothIcon,
    iconActive: Cog6ToothIconSolid,
    requireAuth: true,
  },
];

const SWIPE_THRESHOLD = 50;
const SCROLL_HIDE_THRESHOLD = 12;
const TOP_SHOW_THRESHOLD = 24;
/** Pixels of scroll delta to travel from full bar → compact pill. */
const COLLAPSE_SCROLL_RANGE = 110;
const EXPAND_ANIMATION_MS = 320;

const FULL_NAV_HEIGHT = "calc(4rem + 1rem + env(safe-area-inset-bottom))";

function clamp(value: number, min: number, max: number) {
  return Math.min(max, Math.max(min, value));
}

function lerp(a: number, b: number, t: number) {
  return a + (b - a) * t;
}

function smoothstep(edge0: number, edge1: number, x: number) {
  const t = clamp((x - edge0) / (edge1 - edge0), 0, 1);
  return t * t * (3 - 2 * t);
}

function easeOutCubic(t: number) {
  return 1 - Math.pow(1 - t, 3);
}

function pickConsumerItems(hrefs: readonly string[]): BottomNavItem[] {
  return hrefs.map((href) => {
    const item = consumerMenuItems.find((i) => i.href === href);
    if (!item) {
      throw new Error(`Missing consumer nav item for href: ${href}`);
    }
    return item;
  });
}

function isAdminBottomNavActive(pathname: string, href: string): boolean {
  if (href === "/admin") {
    return pathname === "/admin";
  }
  return pathname.startsWith(href);
}

function isConsumerItemActive(pathname: string, href: string): boolean {
  if (href === "/") {
    return pathname === "/";
  }
  return pathname === href || pathname.startsWith(`${href}/`);
}

const MobileMenu = () => {
  const pathname = usePathname();
  const { isAuthenticated, user, isAdmin } = useAuth();
  const {
    needsAttention: myItemsNeedsAttention,
    itemsNeedingAttentionCount: myItemsAttentionCount,
  } = useSellerListingAttentionContext();
  const { count: adminAttentionQueueCount } =
    useAdminProductsAttentionContext();
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url);
  const { navMode, setNavMode, setIsMenuVisible } = useMobileMenu();
  const [collapseProgress, setCollapseProgress] = useState(0);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  const collapseProgressRef = useRef(0);
  const touchStartY = useRef(0);
  const touchEndY = useRef(0);
  const lastScrollY = useRef(0);
  const ticking = useRef(false);
  const isAnimatingRef = useRef(false);
  const animationFrameRef = useRef<number | null>(null);

  const isAdminRouteNav = isAdmin && pathname.startsWith("/admin");
  const usesCompactNav = !isAdminRouteNav;

  const menuItems: BottomNavItem[] = isAdminRouteNav
    ? adminMobileNav
    : isAuthenticated
      ? pickConsumerItems(AUTHED_CONSUMER_ORDER)
      : pickConsumerItems(GUEST_CONSUMER_ORDER);

  const fiveCols = isAuthenticated || isAdminRouteNav;

  const activeConsumerItem = useMemo(() => {
    if (isAdminRouteNav) return null;
    return (
      menuItems.find((item) => isConsumerItemActive(pathname, item.href)) ??
      menuItems[0]
    );
  }, [isAdminRouteNav, menuItems, pathname]);

  const syncNavMode = useCallback(
    (progress: number) => {
      if (!usesCompactNav) return;
      if (progress >= 0.92) {
        setNavMode("compact");
      } else if (progress <= 0.08) {
        setNavMode("full");
      }
    },
    [setNavMode, usesCompactNav],
  );

  const applyCollapseProgress = useCallback(
    (next: number) => {
      const clamped = clamp(next, 0, 1);
      collapseProgressRef.current = clamped;
      setCollapseProgress(clamped);
      syncNavMode(clamped);

      const shellHeightRem = lerp(4, 3, clamped);
      document.documentElement.style.setProperty(
        "--mobile-bottom-nav-height",
        `calc(${shellHeightRem}rem + 1rem + env(safe-area-inset-bottom))`,
      );
    },
    [syncNavMode],
  );

  const cancelAnimation = useCallback(() => {
    if (animationFrameRef.current !== null) {
      cancelAnimationFrame(animationFrameRef.current);
      animationFrameRef.current = null;
    }
    isAnimatingRef.current = false;
  }, []);

  const animateCollapseProgress = useCallback(
    (target: number, durationMs = EXPAND_ANIMATION_MS) => {
      cancelAnimation();
      isAnimatingRef.current = true;

      const start = collapseProgressRef.current;
      const startTime = performance.now();

      const step = (now: number) => {
        const elapsed = now - startTime;
        const t = clamp(elapsed / durationMs, 0, 1);
        const eased = easeOutCubic(t);
        const next = lerp(start, target, eased);
        applyCollapseProgress(next);

        if (t < 1) {
          animationFrameRef.current = requestAnimationFrame(step);
        } else {
          isAnimatingRef.current = false;
          animationFrameRef.current = null;
        }
      };

      animationFrameRef.current = requestAnimationFrame(step);
    },
    [applyCollapseProgress, cancelAnimation],
  );

  const handleNavPointerDown = (e: React.PointerEvent<HTMLElement>) => {
    if (e.defaultPrevented) return;
    if (e.pointerType === "mouse" && e.button !== 0) return;
    lightTap();
  };

  const handleExpandFromCompact = () => {
    lightTap();
    if (prefersReducedMotion) {
      applyCollapseProgress(0);
      return;
    }
    animateCollapseProgress(0);
  };

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setAvatarUrl(user?.avatar_url);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [user]);

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setPrefersReducedMotion(media.matches);
    update();
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (!isAdminRouteNav) return;
    cancelAnimation();
    const timer = window.setTimeout(() => {
      applyCollapseProgress(0);
    }, 0);
    return () => window.clearTimeout(timer);
  }, [applyCollapseProgress, cancelAnimation, isAdminRouteNav]);

  useEffect(() => {
    if (!usesCompactNav) {
      document.documentElement.style.setProperty(
        "--mobile-bottom-nav-height",
        FULL_NAV_HEIGHT,
      );
      return;
    }

    applyCollapseProgress(collapseProgressRef.current);

    return () => {
      document.documentElement.style.setProperty(
        "--mobile-bottom-nav-height",
        FULL_NAV_HEIGHT,
      );
    };
  }, [applyCollapseProgress, usesCompactNav]);

  useEffect(() => {
    return () => cancelAnimation();
  }, [cancelAnimation]);

  useEffect(() => {
    const handleTouchStart = (e: TouchEvent) => {
      touchStartY.current = e.touches[0].clientY;
    };

    const handleTouchEnd = (e: TouchEvent) => {
      touchEndY.current = e.changedTouches[0].clientY;
      const swipeDistance = touchStartY.current - touchEndY.current;

      if (usesCompactNav) {
        if (swipeDistance > SWIPE_THRESHOLD) {
          if (prefersReducedMotion) {
            applyCollapseProgress(1);
          } else {
            animateCollapseProgress(1, 240);
          }
        } else if (swipeDistance < -SWIPE_THRESHOLD) {
          if (prefersReducedMotion) {
            applyCollapseProgress(0);
          } else {
            animateCollapseProgress(0);
          }
        }
        return;
      }

      if (swipeDistance > SWIPE_THRESHOLD && navMode !== "hidden") {
        setIsMenuVisible(false);
      } else if (swipeDistance < -SWIPE_THRESHOLD && navMode === "hidden") {
        setIsMenuVisible(true);
      }
    };

    document.addEventListener("touchstart", handleTouchStart);
    document.addEventListener("touchend", handleTouchEnd);

    return () => {
      document.removeEventListener("touchstart", handleTouchStart);
      document.removeEventListener("touchend", handleTouchEnd);
    };
  }, [
    animateCollapseProgress,
    applyCollapseProgress,
    navMode,
    prefersReducedMotion,
    setIsMenuVisible,
    usesCompactNav,
  ]);

  useEffect(() => {
    lastScrollY.current = window.scrollY;

    const handleScroll = () => {
      const currentY = window.scrollY;
      const delta = currentY - lastScrollY.current;

      if (!ticking.current) {
        window.requestAnimationFrame(() => {
          if (usesCompactNav) {
            if (!isAnimatingRef.current) {
              if (currentY <= TOP_SHOW_THRESHOLD) {
                applyCollapseProgress(0);
              } else if (Math.abs(delta) >= 1) {
                const scrollStep = delta / COLLAPSE_SCROLL_RANGE;
                const next = prefersReducedMotion
                  ? delta > SCROLL_HIDE_THRESHOLD
                    ? 1
                    : delta < -SCROLL_HIDE_THRESHOLD
                      ? 0
                      : collapseProgressRef.current
                  : collapseProgressRef.current + scrollStep;
                applyCollapseProgress(next);
              }
            }
          } else if (currentY <= TOP_SHOW_THRESHOLD) {
            setIsMenuVisible(true);
          } else if (delta > SCROLL_HIDE_THRESHOLD) {
            setIsMenuVisible(false);
          } else if (delta < -SCROLL_HIDE_THRESHOLD) {
            setIsMenuVisible(true);
          }

          lastScrollY.current = currentY;
          ticking.current = false;
        });
        ticking.current = true;
      } else {
        lastScrollY.current = currentY;
      }
    };

    window.addEventListener("scroll", handleScroll, { passive: true });

    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, [
    applyCollapseProgress,
    prefersReducedMotion,
    setIsMenuVisible,
    usesCompactNav,
  ]);

  const isFullyHidden = isAdminRouteNav && navMode === "hidden";

  const renderAdminNavItem = (item: BottomNavItem) => {
    const isActive = isAdminBottomNavActive(pathname, item.href);
    const itemHref =
      item.requireAuth && !isAuthenticated
        ? `/login?redirectTo=${item.href}`
        : item.href;
    const Icon = isActive ? item.iconActive : item.icon;
    const showAdminProductsBadge =
      item.href === "/admin/products" && adminAttentionQueueCount > 0;

    return (
      <Link
        key={item.name}
        href={itemHref}
        className="flex flex-col items-center justify-center px-2 py-1 rounded-lg mx-0.5 hover:bg-secondary/80 group"
        onPointerDown={handleNavPointerDown}
        aria-label={
          showAdminProductsBadge
            ? `${item.name}, ${adminAttentionQueueCount} need review`
            : item.name
        }
      >
        <span className="relative inline-flex">
          <Icon
            className={`w-6 h-6 ${isActive ? "text-primary" : "text-text-light group-hover:text-foreground"}`}
          />
          {showAdminProductsBadge ? (
            <span
              className="absolute -top-1 -right-2 min-w-[1rem] rounded-full bg-cyan-600 px-1 text-[9px] font-semibold leading-4 text-white ring-2 ring-surface"
              aria-hidden
            >
              {adminAttentionQueueCount > 9 ? "9+" : adminAttentionQueueCount}
            </span>
          ) : null}
        </span>
        <span
          className={`text-xs ${isActive ? "text-primary font-medium" : "text-text-light group-hover:text-foreground"}`}
        >
          {item.name}
        </span>
      </Link>
    );
  };

  const renderConsumerMorphNav = () => {
    const p = collapseProgress;
    const fullOpacity = 1 - smoothstep(0.28, 0.62, p);
    const compactOpacity = smoothstep(0.38, 0.72, p);
    const shellHeight = lerp(64, 48, p);
    const shellRadius = lerp(16, 9999, p);
    const shellWidth = `calc((1 - ${p}) * 100% + ${p} * 11.5rem)`;
    const compactInteractive = p >= 0.55;

    return (
      <nav
        className="sio-glass relative mx-auto overflow-hidden shadow-md"
        style={{
          height: shellHeight,
          width: shellWidth,
          borderRadius: shellRadius,
          boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)",
        }}
        aria-label="Main navigation"
      >
        {/* Full tab bar — fades out while the shell shrinks */}
        <div
          className={`absolute inset-0 grid h-full ${
            fiveCols ? "grid-cols-5" : "grid-cols-3"
          }`}
          style={{
            opacity: fullOpacity,
            pointerEvents: fullOpacity > 0.35 ? "auto" : "none",
          }}
          aria-hidden={fullOpacity < 0.5}
        >
          {menuItems.map((item) => {
            const isActive = isConsumerItemActive(pathname, item.href);
            const itemHref =
              item.requireAuth && !isAuthenticated
                ? `/login?redirectTo=${item.href}`
                : item.href;
            const Icon = isActive ? item.iconActive : item.icon;
            const showMyItemsDot =
              item.href === "/my-stuff" &&
              isAuthenticated &&
              myItemsNeedsAttention;

            if (item.isProfile && avatarUrl) {
              return (
                <Link
                  key={item.name}
                  href={itemHref}
                  className="flex flex-col items-center justify-center px-2 py-1 rounded-lg mx-0.5 hover:bg-secondary/80 group"
                  onPointerDown={handleNavPointerDown}
                >
                  <div className="w-6 h-6 relative rounded-full overflow-hidden">
                    <Image
                      src={avatarUrl}
                      alt="Profile"
                      fill
                      sizes="24px"
                      className="object-cover"
                      unoptimized={process.env.NODE_ENV === "development"}
                    />
                  </div>
                  <span
                    className={`text-xs ${isActive ? "text-primary font-medium" : "text-text-light group-hover:text-foreground"}`}
                  >
                    {item.name}
                  </span>
                </Link>
              );
            }

            return (
              <Link
                key={item.name}
                href={itemHref}
                className="flex flex-col items-center justify-center px-2 py-1 rounded-lg mx-0.5 hover:bg-secondary/80 group"
                onPointerDown={handleNavPointerDown}
                aria-label={
                  showMyItemsDot
                    ? `${item.name}, attention needed on a listing`
                    : item.name
                }
              >
                <span className="relative inline-flex">
                  <Icon
                    className={`w-6 h-6 ${isActive ? "text-primary" : "text-text-light group-hover:text-foreground"}`}
                  />
                  {showMyItemsDot ? (
                    <SellerMyItemsAttentionNavIndicator
                      show
                      count={myItemsAttentionCount}
                      compact
                    />
                  ) : null}
                </span>
                <span
                  className={`text-xs ${isActive ? "text-primary font-medium" : "text-text-light group-hover:text-foreground"}`}
                >
                  {item.name}
                </span>
              </Link>
            );
          })}
        </div>

        {/* Compact pill — fades in as the shell finishes shrinking */}
        {activeConsumerItem ? (
          <button
            type="button"
            onClick={handleExpandFromCompact}
            className="absolute inset-0 flex h-full w-full items-center justify-center gap-2 px-4 hover:bg-secondary/40"
            style={{
              opacity: compactOpacity,
              pointerEvents: compactInteractive ? "auto" : "none",
            }}
            aria-label={`Open navigation — currently on ${activeConsumerItem.name}`}
            aria-expanded={collapseProgress < 0.5}
            tabIndex={compactInteractive ? 0 : -1}
          >
            {activeConsumerItem.isProfile && avatarUrl ? (
              <div className="relative h-6 w-6 shrink-0 overflow-hidden rounded-full">
                <Image
                  src={avatarUrl}
                  alt=""
                  fill
                  sizes="24px"
                  className="object-cover"
                  aria-hidden
                  unoptimized={process.env.NODE_ENV === "development"}
                />
              </div>
            ) : (
              <span className="relative inline-flex shrink-0">
                {(() => {
                  const isActive = isConsumerItemActive(
                    pathname,
                    activeConsumerItem.href,
                  );
                  const Icon = isActive
                    ? activeConsumerItem.iconActive
                    : activeConsumerItem.icon;
                  const showMyItemsDot =
                    activeConsumerItem.href === "/my-stuff" &&
                    isAuthenticated &&
                    myItemsNeedsAttention;
                  return (
                    <>
                      <Icon className="h-6 w-6 text-primary" aria-hidden />
                      {showMyItemsDot ? (
                        <SellerMyItemsAttentionNavIndicator
                          show
                          count={myItemsAttentionCount}
                          compact
                        />
                      ) : null}
                    </>
                  );
                })()}
              </span>
            )}
            <span className="max-w-[9rem] truncate text-sm font-medium text-foreground">
              {activeConsumerItem.name}
            </span>
            <ChevronUpIcon
              className="h-4 w-4 shrink-0 text-text-light"
              aria-hidden
            />
          </button>
        ) : null}
      </nav>
    );
  };

  return (
    <div
      className={`fixed inset-x-0 z-50 px-4 lg:hidden transition-transform duration-300 ease-out motion-reduce:transition-none ${
        isFullyHidden
          ? "translate-y-[calc(100%+1rem+env(safe-area-inset-bottom))]"
          : "translate-y-0"
      }`}
      style={{
        bottom: "calc(1rem + env(safe-area-inset-bottom))",
      }}
    >
      {usesCompactNav ? (
        renderConsumerMorphNav()
      ) : (
        <nav
          className="sio-glass h-16 w-full rounded-sio-lg shadow-md"
          style={{ boxShadow: "0px 0px 10px 0px rgba(0, 0, 0, 0.1)" }}
          aria-label="Main navigation"
        >
          <div
            className={`grid h-full ${fiveCols ? "grid-cols-5" : "grid-cols-3"}`}
          >
            {menuItems.map((item) => renderAdminNavItem(item))}
          </div>
        </nav>
      )}
    </div>
  );
};

export default MobileMenu;
